import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from "react";
import { getSupabase, type Session, type User } from "@dwelldrop/supabase";
import {
  buildMfaTwoFactorFromSession,
  createInitialTwoFactorState,
  hydrateMfaAfterSessionRestore,
  logAuditEvent,
  validateJwtOnLoad,
  useForceLogout,
  getAdminSessionDuration,
  isRememberMe,
  saveLoginTimestamp,
  clearLoginTimestamp,
  isSessionExpired,
  getAdminPermissionsForRole,
  type AdminRoleType,
  type MfaRateLimitState,
  type MfaTotpEnrollResult,
  type TwoFactorState,
} from "@dwelldrop/shared";
import { auditActionSource } from "../utils/auditActionSource";
import {
  adminComputeMfaLockedOut,
  adminSignInWithPassword,
  createAdminMfaHandlers,
  fetchAdminMfaProfile,
  type AdminPendingSession,
} from "../services/adminAuthMfa";
import { ADMIN_MFA_CONFIG } from "../services/adminMfaConfig";

type UserRole =
  | "consumer"
  | "venue_owner"
  | "venue_staff"
  | "admin"
  | "mall_operator"
  | "property_operator";

type AdminRole = AdminRoleType;

interface AdminAuthState {
  user: User | null;
  session: Session | null;
  role: UserRole | null;
  adminRole: AdminRole | null;
  permissions: string[];
  loading: boolean;
  twoFactor: TwoFactorState;
}

interface AdminAuthContextValue extends AdminAuthState {
  signIn: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<{ error: string | null; needs2FA?: boolean }>;
  verify2FA: (code: string) => Promise<{ error: string | null }>;
  enable2FAEnrollTotp: () => Promise<MfaTotpEnrollResult>;
  enable2FAVerify: (params: {
    code: string;
    factorId: string;
    challengeId?: string;
    phone?: string;
  }) => Promise<{ error: string | null; recoveryCodes?: string[] }>;
  disable2FA: () => Promise<{ error: string | null }>;
  disable2FASendChallenge: () => Promise<{
    error: string | null;
    factorId: string | null;
    challengeId: string | null;
  }>;
  disable2FAVerify: (params: {
    code: string;
    factorId: string;
    challengeId: string;
  }) => Promise<{ error: string | null }>;
  generateRecoveryCodes: () => Promise<{
    error: string | null;
    recoveryCodes: string[];
  }>;
  verifyRecoveryCode: (code: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isLockedOut: () => boolean;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx)
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}

async function fetchUserRole(userId: string): Promise<{
  role: UserRole | null;
  adminRole: AdminRole | null;
  permissions: string[];
}> {
  const supabase = getSupabase(ADMIN_MFA_CONFIG.tokenStorageKey);
  const { data, error } = await supabase
    .from("profiles")
    .select("role, admin_role, deleted_at")
    .eq("id", userId)
    .single();
  if (error || !data || data.deleted_at)
    return { role: null, adminRole: null, permissions: [] };
  const role = data.role as UserRole;
  const adminRole = (data.admin_role as AdminRole) ?? null;
  const permissions =
    role === "admin" && adminRole
      ? getAdminPermissionsForRole(adminRole)
      : [];
  return { role, adminRole, permissions };
}

const emptyTwoFactor: TwoFactorState = createInitialTwoFactorState();

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminAuthState>({
    user: null,
    session: null,
    role: null,
    adminRole: null,
    permissions: [],
    loading: true,
    twoFactor: emptyTwoFactor,
  });

  const [rateLimit, setRateLimit] = useState<MfaRateLimitState>({
    smsSendCount: 0,
    smsSendWindowStart: 0,
    failedAttempts: 0,
    lockedUntil: null,
  });

  const [pendingSession, setPendingSession] =
    useState<AdminPendingSession | null>(null);
  const signInInProgress = useRef(false);
  const pendingRef = useRef(false);
  const pendingMfaRef = useRef(false);
  const mfaCompletingRef = useRef(false);

  useEffect(() => {
    pendingRef.current = state.twoFactor.pendingVerification;
  }, [state.twoFactor.pendingVerification]);

  const isLockedOut = useCallback(
    () => adminComputeMfaLockedOut(rateLimit.lockedUntil, setRateLimit),
    [rateLimit.lockedUntil]
  );

  const completeAdminSession = useCallback(
    async (session: Session) => {
      const { role, adminRole, permissions } = await fetchUserRole(
        session.user.id
      );
      const profile = await fetchAdminMfaProfile(session.user.id);
      let twoFactor: TwoFactorState = createInitialTwoFactorState();
      if (profile) {
        const hydrated = await hydrateMfaAfterSessionRestore({
          config: ADMIN_MFA_CONFIG,
          user: session.user,
          session,
          profile,
          rememberMe: isRememberMe("venuehub_admin"),
          rateLimit,
          setRateLimit,
        });
        if (hydrated.needsMfaChallenge && hydrated.pendingSession) {
          setPendingSession(hydrated.pendingSession);
        }
        twoFactor = {
          ...createInitialTwoFactorState(),
          ...hydrated.twoFactor,
        };
      } else {
        const twoFactorBits = await buildMfaTwoFactorFromSession(
          ADMIN_MFA_CONFIG.tokenStorageKey,
          null
        );
        twoFactor = {
          ...createInitialTwoFactorState(),
          ...twoFactorBits,
        };
      }
      setState({
        user: session.user,
        session,
        role,
        adminRole,
        permissions,
        loading: false,
        twoFactor,
      });
    },
    [rateLimit, setRateLimit]
  );

  const onMfaLoginComplete = useCallback(
    async (pending: AdminPendingSession, session: Session) => {
      mfaCompletingRef.current = true;
      pendingMfaRef.current = false;
      try {
        saveLoginTimestamp("venuehub_admin", pending.rememberMe);
        setState((s) => ({
          ...s,
          user: session.user,
          session,
          loading: true,
          twoFactor: {
            ...s.twoFactor,
            pendingVerification: false,
          },
        }));
        const { role, adminRole, permissions } = await fetchUserRole(
          session.user.id
        );
        const profile = await fetchAdminMfaProfile(session.user.id);
        const twoFactorBits = await buildMfaTwoFactorFromSession(
          ADMIN_MFA_CONFIG.tokenStorageKey,
          profile?.phone ?? null
        );
        setState({
          user: session.user,
          session,
          role,
          adminRole,
          permissions,
          loading: false,
          twoFactor: {
            ...createInitialTwoFactorState(),
            ...twoFactorBits,
          },
        });
      } finally {
        mfaCompletingRef.current = false;
      }
    },
    []
  );

  const mfaHandlers = useMemo(
    () =>
      createAdminMfaHandlers({
        pendingSession,
        rateLimit,
        setRateLimit,
        setPendingSession,
        setTwoFactor: (action) => {
          setState((s) => ({
            ...s,
            twoFactor:
              typeof action === "function" ? action(s.twoFactor) : action,
          }));
        },
        isLockedOut,
        twoFactor: state.twoFactor,
        onMfaLoginComplete,
        userId: state.user?.id ?? null,
      }),
    [
      pendingSession,
      rateLimit,
      isLockedOut,
      onMfaLoginComplete,
      state.user?.id,
      state.twoFactor,
    ]
  );

  useEffect(() => {
    let supabase: ReturnType<typeof getSupabase>;
    try {
      supabase = getSupabase(ADMIN_MFA_CONFIG.tokenStorageKey);
    } catch {
      setState({
        user: null,
        session: null,
        role: null,
        adminRole: null,
        permissions: [],
        loading: false,
        twoFactor: emptyTwoFactor,
      });
      return;
    }

    let cancelled = false;
    let initialSessionResolved = false;

    validateJwtOnLoad(supabase, "/login", async () => {
      await logAuditEvent(
        supabase,
        "jwt_tamper_detected",
        null,
        {},
        "auth",
        "/login"
      );
    });

    const timeout = setTimeout(() => {
      setState((prev) =>
        prev.loading
          ? {
              user: null,
              session: null,
              role: null,
              adminRole: null,
              permissions: [],
              loading: false,
              twoFactor: emptyTwoFactor,
            }
          : prev
      );
    }, 5000);

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      clearTimeout(timeout);
      initialSessionResolved = true;
      if (session?.user) {
        if (pendingRef.current || pendingMfaRef.current) {
          setState((s) => ({ ...s, loading: false }));
          return;
        }
        // Persisted AAL1 session with an unsatisfied MFA challenge means the
        // user closed/refreshed mid-verification. Sign out and force re-login
        // instead of silently re-issuing an SMS challenge.
        const bits = await buildMfaTwoFactorFromSession(
          ADMIN_MFA_CONFIG.tokenStorageKey,
          null
        );
        if (bits.pendingVerification) {
          await supabase.auth.signOut();
          if (cancelled) return;
          setState({
            user: null,
            session: null,
            role: null,
            adminRole: null,
            permissions: [],
            loading: false,
            twoFactor: emptyTwoFactor,
          });
          return;
        }
        await completeAdminSession(session);
        if (!cancelled) {
          void logAuditEvent(
            supabase,
            "session_created",
            session.user.id,
            { app: "admin" },
            "auth",
            auditActionSource()
          );
        }
      } else {
        setState({
          user: null,
          session: null,
          role: null,
          adminRole: null,
          permissions: [],
          loading: false,
          twoFactor: emptyTwoFactor,
        });
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!initialSessionResolved || cancelled) return;
      if (event === "MFA_CHALLENGE_VERIFIED") return;
      if (signInInProgress.current) return;
      if (pendingRef.current) return;
      if (pendingMfaRef.current) return;
      if (mfaCompletingRef.current) return;

      if (event === "TOKEN_REFRESHED") {
        if (isSessionExpired(
            "venuehub_admin",
            getAdminSessionDuration(isRememberMe("venuehub_admin"))
          )) {
          clearLoginTimestamp("venuehub_admin");
          await supabase.auth.signOut();
          window.location.href = "/login?expired=true";
          return;
        }
        setState((s) => ({ ...s, session: session ?? s.session }));
        return;
      }
      if (event === "INITIAL_SESSION" || event === "USER_UPDATED") return;
      if (session?.user) {
        await completeAdminSession(session);
      } else {
        setState({
          user: null,
          session: null,
          role: null,
          adminRole: null,
          permissions: [],
          loading: false,
          twoFactor: emptyTwoFactor,
        });
        setPendingSession(null);
      }
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (isSessionExpired(
            "venuehub_admin",
            getAdminSessionDuration(isRememberMe("venuehub_admin"))
          )) {
          clearLoginTimestamp("venuehub_admin");
          supabase.auth.signOut();
          window.location.href = "/login?expired=true";
          return;
        }
        supabase.auth.startAutoRefresh();
      } else {
        supabase.auth.stopAutoRefresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [completeAdminSession]);

  const signIn = useCallback(
    async (email: string, password: string, rememberMe = false) => {
      pendingMfaRef.current = true;
      const result = await adminSignInWithPassword({
        email,
        password,
        rateLimit,
        setRateLimit,
        isLockedOut,
        setPendingSession,
        setTwoFactor: (action) => {
          setState((s) => ({
            ...s,
            twoFactor:
              typeof action === "function" ? action(s.twoFactor) : action,
          }));
        },
        signInInProgress,
        rememberMe,
      });

      if (!result.needs2FA) {
        pendingMfaRef.current = false;
      }

      if (!result.needs2FA && !result.error) {
        const supabase = getSupabase(ADMIN_MFA_CONFIG.tokenStorageKey);
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          await completeAdminSession(session);
        }
      }

      return result;
    },
    [rateLimit, isLockedOut, completeAdminSession]
  );

  const signOut = useCallback(async () => {
    pendingMfaRef.current = false;
    const supabase = getSupabase(ADMIN_MFA_CONFIG.tokenStorageKey);
    await logAuditEvent(
      supabase,
      "logout",
      state.user?.id ?? null,
      { app: "admin" },
      "auth",
      auditActionSource()
    );
    await supabase.auth.signOut();
    clearLoginTimestamp("venuehub_admin");
    try {
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split("; ").forEach((c) => {
        const name = c.split("=")[0];
        if (name.startsWith("venuehub_") && name.includes("token")) {
          document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Strict`;
        }
      });
    } catch {
      // Storage might be restricted
    }
    setState({
      user: null,
      session: null,
      role: null,
      adminRole: null,
      permissions: [],
      loading: false,
      twoFactor: emptyTwoFactor,
    });
    setPendingSession(null);
  }, [state.user]);

  const supabaseForLogout = state.user
    ? getSupabase(ADMIN_MFA_CONFIG.tokenStorageKey)
    : null;
  useForceLogout(supabaseForLogout, state.user?.id ?? null, signOut);

  const isAdmin = state.role === "admin";

  return (
    <AdminAuthContext.Provider
      value={{
        ...state,
        signIn,
        verify2FA: mfaHandlers.verify2FA,
        enable2FAEnrollTotp: mfaHandlers.enable2FAEnrollTotp,
        enable2FAVerify: mfaHandlers.enable2FAVerify,
        disable2FA: mfaHandlers.disable2FA,
        disable2FASendChallenge: mfaHandlers.disable2FASendChallenge,
        disable2FAVerify: mfaHandlers.disable2FAVerify,
        generateRecoveryCodes: mfaHandlers.generateRecoveryCodes,
        verifyRecoveryCode: mfaHandlers.verifyRecoveryCode,
        signOut,
        isAdmin,
        isLockedOut,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}
